"use client";

import { useEffect, useRef, useState, useTransition } from "react";
import {
  captureSnapshot,
  checkSnapshot,
} from "@/actions/tools/capture-snapshot";
import { Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TurnstileChallenge } from "@/components/security/turnstile-challenge";
import { useTurnstileSession } from "@/hooks/use-turnstile-session";
import { ToolRegistrationCTA } from "@/components/tools/tool-registration-cta";
import { SocialCardPreviews } from "./social-card-previews";
import { ImageModal } from "@/components/shared/image-modal";

// Fixed Open Graph viewport: 1200×630 at 1x DPR
const OG_VIEWPORT = {
  width: 1200,
  height: 630,
  deviceScaleFactor: 1,
};

interface ScreenshotState {
  url: string;
  snapshotId?: string;
  status?: string;
  screenshotUrl?: string;
  error?: string;
}

interface PollControl {
  token: number;
  timer?: ReturnType<typeof setTimeout>;
}

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 120000;

export function OgScreenshotGenerator() {
  const [state, setState] = useState<ScreenshotState>({
    url: "",
  });
  const [selectedImage, setSelectedImage] = useState<{ src: string; alt: string } | null>(null);
  const [isPending, startTransition] = useTransition();
  const [isPolling, setIsPolling] = useState(false);
  const pollControl = useRef<PollControl>({ token: 0 });
  const { isReady: isTurnstileReady } = useTurnstileSession();

  useEffect(() => {
    const control = pollControl.current;
    return () => {
      if (control.timer) {
        clearTimeout(control.timer);
      }
    };
  }, []);

  const clearPollingTimer = () => {
    if (pollControl.current.timer) {
      clearTimeout(pollControl.current.timer);
      pollControl.current.timer = undefined;
    }
  };

  const schedulePoll = (
    token: number,
    snapshotId: string,
    startedAt: number,
  ) => {
    pollControl.current.timer = setTimeout(async () => {
      if (pollControl.current.token !== token) {
        return;
      }

      if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
        pollControl.current.timer = undefined;
        setIsPolling(false);
        setState((previous) => ({
          ...previous,
          error: "Timed out waiting for screenshot",
          status: undefined,
          snapshotId: undefined,
          screenshotUrl: undefined,
        }));
        return;
      }

      try {
        const result = await checkSnapshot({
          snapshotId,
          timeout: POLL_INTERVAL_MS,
        });

        if (pollControl.current.token !== token) {
          return;
        }

        if (result.success) {
          setState((previous) => ({
            ...previous,
            snapshotId: result.snapshotId,
            status: result.status,
            screenshotUrl: result.screenshotUrl ?? previous.screenshotUrl,
            error: undefined,
          }));

          if (result.status === "COMPLETE") {
            pollControl.current.timer = undefined;
            setIsPolling(false);
            return;
          }

          schedulePoll(token, snapshotId, startedAt);
        } else {
          pollControl.current.timer = undefined;
          setIsPolling(false);
          setState((previous) => ({
            ...previous,
            error: result.error,
            status: undefined,
            snapshotId: undefined,
            screenshotUrl: undefined,
          }));
        }
      } catch (error) {
        if (pollControl.current.token !== token) {
          return;
        }

        pollControl.current.timer = undefined;
        setIsPolling(false);
        setState((previous) => ({
          ...previous,
          error: error instanceof Error ? error.message : "Unexpected error",
          status: undefined,
          snapshotId: undefined,
          screenshotUrl: undefined,
        }));
      }
    }, POLL_INTERVAL_MS);
  };

  const handleGenerate = () => {
    const url = state.url.trim();
    if (!url) {
      setState((previous) => ({
        ...previous,
        error: "Please provide a public URL.",
      }));
      return;
    }

    if (!isTurnstileReady) {
      setState((previous) => ({
        ...previous,
        error: "Complete the Turnstile challenge before generating.",
      }));
      return;
    }

    const nextToken = pollControl.current.token + 1;
    pollControl.current.token = nextToken;
    clearPollingTimer();
    setIsPolling(false);

    setState((previous) => ({
      ...previous,
      error: undefined,
      snapshotId: undefined,
      status: undefined,
      screenshotUrl: undefined,
    }));

    startTransition(() => {
      captureSnapshot({
        url,
        viewport: OG_VIEWPORT,
        options: {
          format: "jpeg",
          imageOptions: {
            fullPage: false,
            quality: 100,
          },
          block: {
            cookieBanners: true,
            chats: true,
            ads: true,
            trackers: true,
          }
        },
      })
        .then((result) => {
          if (pollControl.current.token !== nextToken) {
            return;
          }

          if (result.success) {
            setState((previous) => ({
              ...previous,
              snapshotId: result.snapshotId,
              status: result.status,
              screenshotUrl: result.screenshotUrl ?? previous.screenshotUrl,
              error: undefined,
            }));

            if (result.status === "COMPLETE") {
              setIsPolling(false);
              return;
            }

            setIsPolling(true);
            schedulePoll(nextToken, result.snapshotId, Date.now());
          } else {
            setIsPolling(false);
            setState((previous) => ({
              ...previous,
              error: result.error,
              snapshotId: undefined,
              status: undefined,
              screenshotUrl: undefined,
            }));
          }
        })
        .catch((error) => {
          if (pollControl.current.token !== nextToken) {
            return;
          }

          setIsPolling(false);
          setState((previous) => ({
            ...previous,
            error: error instanceof Error ? error.message : "Unexpected error",
            snapshotId: undefined,
            status: undefined,
            screenshotUrl: undefined,
          }));
        });
    });
  };

  const statusMessage = (() => {
    if (state.error) {
      return state.error;
    }

    if (isPending) {
      return "Submitting capture request...";
    }

    if (isPolling) {
      return "Processing screenshot, hang tight...";
    }

    if (!isTurnstileReady) {
      return "Complete the Turnstile challenge to unlock the generator.";
    }

    if (state.status === "COMPLETE") {
      return "Screenshot ready.";
    }

    if (state.status === "QUEUED") {
      return "Waiting for the capture to finish...";
    }

    return "Enter a public URL to generate an Open Graph image.";
  })();

  return (
    <div className="space-y-8">
      <TurnstileChallenge helperText="Complete this step to protect the free tool from abuse." />
      <Card>
        <CardHeader>
          <CardTitle>Generate Open Graph Image</CardTitle>
          <CardDescription>
            Captures the top 1200×630px of your page — the standard Open Graph
            image size for optimal display on social platforms.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="flex flex-col gap-6"
            onSubmit={(event) => {
              event.preventDefault();
              handleGenerate();
            }}
          >
            <div className="flex flex-col gap-2">
              <Label htmlFor="screenshot-url">Website URL</Label>
              <Input
                id="screenshot-url"
                inputMode="url"
                placeholder="example.com"
                value={state.url}
                onChange={(event) =>
                  setState((previous) => ({
                    ...previous,
                    url: event.target.value,
                  }))
                }
                disabled={isPending}
                required
              />
            </div>

            <div className="flex flex-col gap-2">
              <Button
                type="submit"
                className="w-full sm:w-auto"
                disabled={isPending || isPolling || !isTurnstileReady}
              >
                {isPending || isPolling ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="size-4 animate-spin" />
                    Generating screenshot...
                  </span>
                ) : (
                  "Generate Open Graph Image"
                )}
              </Button>
              <p className="text-sm text-muted-foreground">{statusMessage}</p>
            </div>
          </form>
        </CardContent>
      </Card>

      {state.screenshotUrl ? (
        <>
          <SocialCardPreviews
            imageUrl={state.screenshotUrl}
            url={state.url}
            onImageClick={() => setSelectedImage({ src: state.screenshotUrl!, alt: "Open Graph Image" })}
          />
          <div className="flex flex-wrap justify-center gap-3">
            <Button variant="outline" asChild>
              <a href={state.screenshotUrl} download>
                <span className="flex items-center gap-2">
                  <Download className="size-4" />
                  Download Open Graph Image
                </span>
              </a>
            </Button>
          </div>
          <ToolRegistrationCTA
            title="Like this tool?"
            description="Create a free account to save your screenshots and monitor your pages automatically."
          />
        </>
      ) : null}

      <ImageModal
        src={selectedImage?.src || ""}
        alt={selectedImage?.alt || ""}
        isOpen={!!selectedImage}
        onClose={() => setSelectedImage(null)}
      />
    </div>
  );
}
